import React from "react";
import { useSelector } from "react-redux";
import {
  selectGetEntityOrder,
  selectRowsMap as selectAttributesRowsMap,
} from "state/attributes/selectors";
import { selectRowsMap as selectBetasRowsMap } from "state/betas/selectors";
import { selectRowMap as selectScenarioAnalysisRowsMap } from "state/scenarioAnalysis/selectors";
import { selectRowsMap as selectFactribRowsMap } from "state/factrib/selectors";
import { selectRowsMap as selectPerfReturnsRowsMap } from "state/perfReturns/selectors";
import { selectRowsMap as selectTrackingErrorRowsMap } from "state/te/selectors";
import { selectRowsMap as selectScan2RowsMap } from "state/scan2/selectors";
import { selectRowsMap as selectTradeTrackingErrorRowsMap } from "state/tte/selectors";
import { selectRowsMap as selectDurationToTargetRowsMap } from "state/durationToTarget/selectors";
import { selectRowsMap as selectIssueCountRows } from "state/issueCount/selectors";
import { selectRowsMap as selectIssuerCountRows } from "state/issuerCount/selectors";
import { selectRowsMap as selectMiscRowsMap } from "state/misc/selectors";
import { selectRowsMap as selectBookAmortRowsMap } from "state/bookAnalytics/selectors";
import { useRowsMap as useLastBuySellRowsMap } from "state/lastBuySell/hooks";
import { useRowsMap as useRealizedGlRowsMap } from "state/realizedGL/hooks";
import {
  useRowsMap as useCfRowsMap,
  useColDefs as useCfColDefs,
} from "state/cf/hooks";
import { useColumnDefinitions as useAnalyticsColumnDefinitions } from "state/analytics/hooks";
import { useColumnDefinitions as useBookAnalyticsColumnDefinitions } from "state/bookAnalytics/hooks";
import { useColumnDefs as useScenarioAnalysisColumnDefinitions } from "state/scenarioAnalysis/hooks";
import { useColumnDefinitions as useAttributesColumnDefinitions } from "state/attributes/hooks";
import { useColumnDefinitions as useBetasColumnDefinitions } from "state/betas/hooks";
import { useColumnDefinitions as useDurationToTargetColumnDefinitions } from "state/durationToTarget/hooks";
import { useColumnDefinitions as useFactribDefinitions } from "state/factrib/hooks";
import { useColumnDefinitions as useRatingsColumnDefinitions } from "state/ratings/hooks";
import { useColumnDefinitions as useIssueCountColumnDefinitions } from "state/issueCount/hooks";
import { useColumnDefinitions as useIssuerCountColumnDefinitions } from "state/issuerCount/hooks";
import { useColumnDefinitions as usePerfReturnsColumnDefinitions } from "state/perfReturns/hooks";
import { useColumnDefinitions as useTrackingErrorColumnDefinitions } from "state/te/hooks";
import { useColumnDefinitions as useTradeTrackingErrorColumnDefinitions } from "state/tte/hooks";
import { useColumnDefinitions as usePivotColumnDefinitions } from "state/pivots/hooks";
import { useColumnDefinitions as useMiscColumnDefinitions } from "state/misc/hooks";
import { useColumnDefinitions as useLastBuySellColumnDefinitions } from "state/lastBuySell/hooks";
import { useColumnDefinitions as useRealizedGl } from "state/realizedGL/hooks";
import { selectRowsMap as selectAnalyticsRowsMap } from "state/analytics/selectors";
import {
  useRowsMap as usePivotsRowsMap,
  useDataQueries as usePivotDataQueries,
} from "state/pivots/hooks";
import { useRowsMap as useRatingRowsMap } from "state/ratings/hooks";
import { useColumnDefinitions as useScan2ColumnDefinitions } from "state/scan2/hooks";
import produce from "immer";
import { useColumnDefinitions as useClientLossRstrColumnDefinitions } from "state/clientLossRstr/hooks";
import { useRowsMap as useClientLossRstrMap } from "state/clientLossRstr/hooks";

export const useAllColumnDefinitions = () => {
  const analyticsColumnDefinitions = useAnalyticsColumnDefinitions();
  const bookAnalyticsColumnDefinitions = useBookAnalyticsColumnDefinitions();
  const attributesColumnDefinitions = useAttributesColumnDefinitions();
  const betasColumnDefinitions = useBetasColumnDefinitions();
  const cfColumnDefinitions = useCfColDefs();
  const factribColumnDefinitions = useFactribDefinitions();
  const durationToTargetColumnDefinitions =
    useDurationToTargetColumnDefinitions();
  const ratingColumnDefinitions = useRatingsColumnDefinitions();
  const issueCountDefinitions = useIssueCountColumnDefinitions();
  const perfReturnsColumnDefinitions = usePerfReturnsColumnDefinitions();
  const trackingErrorColumnDefinitions = useTrackingErrorColumnDefinitions();
  const issuerCountDefinitions = useIssuerCountColumnDefinitions();
  const tradeTrackingErrorColumnDefinitions =
    useTradeTrackingErrorColumnDefinitions();
  const pivotColumnDefinitions = usePivotColumnDefinitions();
  const miscColumnDefinitions = useMiscColumnDefinitions();
  const lastBuySellColumnDefinitions = useLastBuySellColumnDefinitions();
  const realizedGlColumnDefinitions = useRealizedGl();
  const scenarioAnalysisColumnDefinitions =
    useScenarioAnalysisColumnDefinitions();
  const scan2ColumnDefinitions = useScan2ColumnDefinitions();
  const clientLossRstrColumnDefinitions = useClientLossRstrColumnDefinitions();

  // Import the pivot data queries to check if they are still fetching.
  const [dataQueries, sbsQueries] = usePivotDataQueries();
  return React.useMemo(() => {
    // Pivot columns are fetched from the server and are not available immediately. If any pivot column is still fetching, return an empty array to prevent the grid from rendering.
    // Otherwise, the grid state column order would be overridden by the pivot columns being pushed to the end of the column definitions.
    if (
      dataQueries.some((query) => query.isFetching) ||
      sbsQueries.some((query) => query.isFetching)
    ) {
      return [];
    }

    const allColumnDefs = [
      ...analyticsColumnDefinitions,
      ...bookAnalyticsColumnDefinitions,
      ...attributesColumnDefinitions,
      ...betasColumnDefinitions,
      ...cfColumnDefinitions,
      ...factribColumnDefinitions,
      ...perfReturnsColumnDefinitions,
      ...trackingErrorColumnDefinitions,
      ...tradeTrackingErrorColumnDefinitions,
      ...durationToTargetColumnDefinitions,
      ...pivotColumnDefinitions,
      ...issueCountDefinitions,
      ...issuerCountDefinitions,
      ...ratingColumnDefinitions,
      ...miscColumnDefinitions,
      ...lastBuySellColumnDefinitions,
      ...scenarioAnalysisColumnDefinitions,
      ...realizedGlColumnDefinitions,
      ...scan2ColumnDefinitions,
      ...clientLossRstrColumnDefinitions,
    ];

    return allColumnDefs.map(
      produce((colDef) => {
        if (colDef.X_sbs && !colDef.headerName) {
          //if coldef is sbs and doesn't have a custom headername, use the header name of the original column to make a better header name
          const originalColDef = allColumnDefs.find(
            (colDefOriginal) => colDefOriginal.colId === colDef.X_originalColId
          );
          if (originalColDef.headerName) {
            colDef.headerName = `Scenario - ${originalColDef.headerName}`;
          }
        }
        return colDef;
      })
    );
  }, [
    analyticsColumnDefinitions,
    bookAnalyticsColumnDefinitions,
    attributesColumnDefinitions,
    betasColumnDefinitions,
    cfColumnDefinitions,
    durationToTargetColumnDefinitions,
    factribColumnDefinitions,
    issueCountDefinitions,
    issuerCountDefinitions,
    miscColumnDefinitions,
    perfReturnsColumnDefinitions,
    pivotColumnDefinitions,
    ratingColumnDefinitions,
    trackingErrorColumnDefinitions,
    tradeTrackingErrorColumnDefinitions,
    lastBuySellColumnDefinitions,
    scenarioAnalysisColumnDefinitions,
    realizedGlColumnDefinitions,
    scan2ColumnDefinitions,
    clientLossRstrColumnDefinitions,
    dataQueries,
    sbsQueries,
  ]);
};

export const useAllRowData = () => {
  const getEntityOrder = useSelector(selectGetEntityOrder);
  const attributesRowsMap = useSelector(selectAttributesRowsMap);
  const analyticsRowsMap = useSelector(selectAnalyticsRowsMap);
  const betasRowsMap = useSelector(selectBetasRowsMap);
  const scenarioAnalysisRowsMap = useSelector(selectScenarioAnalysisRowsMap);
  const cfRowsMap = useCfRowsMap();
  const factribRowsMap = useSelector(selectFactribRowsMap);
  const perfReturnsRowsMap = useSelector(selectPerfReturnsRowsMap);
  const trackingErrorRowsMap = useSelector(selectTrackingErrorRowsMap);
  const tradeTrackingErrorRowsMap = useSelector(
    selectTradeTrackingErrorRowsMap
  );
  const durationToTargetRowsMap = useSelector(selectDurationToTargetRowsMap);
  const pivotRowsMap = usePivotsRowsMap();
  const ratingsRowsMap = useRatingRowsMap();
  const issueCountRows = useSelector(selectIssueCountRows);
  const issuerCountRows = useSelector(selectIssuerCountRows);
  const miscRowsMap = useSelector(selectMiscRowsMap);
  const lastBuySellRowsMap = useLastBuySellRowsMap();
  const realizedGlRowsMap = useRealizedGlRowsMap();
  const scan2RowsMap = useSelector(selectScan2RowsMap);
  const clientLossRstrRowsmap = useClientLossRstrMap();
  const bookAmortRowsMap = useSelector(selectBookAmortRowsMap);
  return React.useMemo(() => {
    return Object.entries(attributesRowsMap).map(([entityId, attributes]) => {
      entityId = Number(entityId);
      const row = {
        ...attributes,
        order: getEntityOrder(entityId) ?? -1,
        entityId,
      };
      [
        analyticsRowsMap,
        betasRowsMap,
        scenarioAnalysisRowsMap,
        cfRowsMap,
        factribRowsMap,
        perfReturnsRowsMap,
        ratingsRowsMap,
        trackingErrorRowsMap,
        tradeTrackingErrorRowsMap,
        durationToTargetRowsMap,
        pivotRowsMap,
        issueCountRows,
        issuerCountRows,
        miscRowsMap,
        lastBuySellRowsMap,
        realizedGlRowsMap,
        scan2RowsMap,
        clientLossRstrRowsmap,
        bookAmortRowsMap,
      ].forEach((rowsMap) => {
        if (rowsMap[entityId]) {
          Object.keys(rowsMap[entityId]).forEach((field) => {
            row[field] = rowsMap[entityId][field];
          });
        }
      });
      return row;
    });
  }, [
    analyticsRowsMap,
    attributesRowsMap,
    betasRowsMap,
    scenarioAnalysisRowsMap,
    cfRowsMap,
    durationToTargetRowsMap,
    factribRowsMap,
    getEntityOrder,
    issueCountRows,
    issuerCountRows,
    lastBuySellRowsMap,
    miscRowsMap,
    perfReturnsRowsMap,
    pivotRowsMap,
    ratingsRowsMap,
    trackingErrorRowsMap,
    tradeTrackingErrorRowsMap,
    realizedGlRowsMap,
    scan2RowsMap,
    clientLossRstrRowsmap,
    bookAmortRowsMap,
  ]);
};
